import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Flex, Box } from 'reflexbox';

import {
  Checkbox,
  Divider,
  Heading,
  Panel,
  PanelHeader,
  Text,
  Tooltip,
} from 'rebass';

import Measures from 'shared/measures';

import GameData from 'shared/gameData';

import Droptimizer from 'shared/droptimizer';
import { getSpecInfo } from 'shared/character';

import {
  Icon,
  If,
  WowItemIcon,
} from 'frontend/components';

import actions from 'frontend/state/actions';
import { selectors as reportSelectors } from 'frontend/state/reducers/simbot/report';

import config from 'frontend/config';

const getDpsMeasure = (measure, field) => `collected_data.${Measures[measure].field}.${field}`;
const getDps = (actor, measure) => _.get(actor, getDpsMeasure(measure, 'mean'));

const formatPct = (pct, digits = 1, showSign = false) => {
  let str = '';
  if (showSign && pct > 0) {
    str += '+';
  }
  const value = 100 * pct;
  let showDigits = digits;
  if (value > 10 || value < -10) {
    showDigits = digits - 1;
  }
  str += value.toLocaleString(
    undefined,
    {
      maximumFractionDigits: showDigits,
      minimumFractionDigits: showDigits,
    },
  );
  return str;
};
const formatDps = (dps, showSign = false) => `${showSign && dps > 0 ? '+' : ''}${_.round(dps).toLocaleString()}`;

const DroptimizerPanel = ({
  droptimizerItems,
  droptimizerOptions,
  actors,
  baseGear,
  classId,
  specId,
  errorMarginPct,
  relativeDps,
  droptimizerSort,
  itemLibrary,
  instanceLibrary,
  measure,

  onChooseSort,
  onChangeRelativeDps,
}) => {
  const upgradeThreshold = errorMarginPct;

  const relativePctColor = value => {
    if (value >= upgradeThreshold) {
      return config.colors.green;
    }

    return config.colors.midgray;
  };

  const dpsColor = dps => {
    if (dps > 0) {
      return config.colors.green;
    }
    return config.colors.orange;
  };

  let instanceId;
  let selectedEncounterId;
  const difficulty = droptimizerOptions.difficulty;



  if (droptimizerOptions.instance) {
    instanceId = droptimizerOptions.instance;
    selectedEncounterId = droptimizerOptions.encounter;
  } else {
    instanceId = _.first(droptimizerOptions.instances);
    selectedEncounterId = _.first(droptimizerOptions.encounters);
  }

  const overrides = Droptimizer.getOverrides(
    instanceId,
    selectedEncounterId,
    difficulty,
  );

  let showChances = true;
  if (_.has(overrides, 'showChances')) {
    showChances = overrides.showChances;
  } else {
    showChances = Droptimizer.shouldCalcRolls({
      repeatable: overrides.repeatable,
      gem: droptimizerOptions.gem,
      warforgeLevel: droptimizerOptions.warforgeLevel,
    });
  }

  const sortProperty = (path, dir) => o => (dir === 'desc' ? -1 : 1) * _.get(o, path);
  let sortOptions = [{
    key: 'priority',
    name: 'Priority',
    func: [sortProperty('rank', 'asc'), sortProperty('upgradeChance', 'desc'), sortProperty('maxPct', 'desc')],
  }, {
    key: 'boss',
    name: `${_.startCase(overrides.encounterType)} Order`,
    func: [sortProperty('encounter.order', 'asc'), sortProperty('encounter.id', 'asc')],
  }, {
    key: 'avg',
    name: 'Expected Value',
    func: [sortProperty('avgPct', 'desc'), sortProperty('maxPct', 'desc')],
  }, {
    key: 'max',
    name: 'Best',
    func: [sortProperty('maxPct', 'desc'), sortProperty('avgPct', 'desc')],
  }];
  const avgSort = _.find(sortOptions, { key: 'avg' }).func;

  if (!showChances) {
    sortOptions = _.reject(sortOptions, { key: 'priority' });
    sortOptions = _.reject(sortOptions, { key: 'avg' });
  }
  if (_.includes(['dungeon', 'container'], overrides.encounterType)) {
    sortOptions = _.reject(sortOptions, { key: 'boss' });
  }


  const [equippedActors, topActorsByItem] = _.partition(
    _.uniqBy(actors, actor => {
      const itemId = _.get(actor, 'droptimizerItem.item.id');
      const itemLevel = _.get(actor, 'droptimizerItem.itemLevel') || 0;
      const encounterId = _.get(actor, 'droptimizerItem.encounter.id');
      const bonusVariationName = _.get(actor, 'droptimizerItem.bonusVariationName');
      return `${itemId}-${itemLevel}-${encounterId}-${bonusVariationName}`;
    }),
    { equipped: true },
  );

  const equipped = _.first(equippedActors);
  const equippedDps = getDps(equipped, measure);

  const actorLookup = _.groupBy(topActorsByItem, 'droptimizerItem.item.id');

  const allItems = itemLibrary || Droptimizer.getItems(droptimizerOptions);

  // get a list of encounterIds from original Droptimizer.getItems - should get bosses with no drops simmed
  const encounterIds = _.uniq(_.map(droptimizerItems, it => {
    const source = _.find(it.sources, { instanceId });
    return source?.encounterId;
  }));

  let encounterStats = _.sortBy(
    _.filter(
      _.map(encounterIds, encounterId => {
        let encounterItems = _.uniqBy(
          _.filter(allItems, i => _.some(i.sources, s => s.encounterId === encounterId)),
          i => `${i.id}-${i.itemLevel}`,
        );

        const actorsWithStats = _.orderBy(
          _.map(encounterItems, item => {
            const possibleActors = actorLookup[item.id];

            // find the item based on the item level. This should handle all weird cases between M+ and sepulcher
            // tier items that could also come from the catalyst
            const actor = _.find(
              possibleActors,
              {
                droptimizerItem: {
                  item: {
                    itemLevel: item.itemLevel,
                  },
                },
              },
            );

            if (actor) {
              const dpsDelta = getDps(actor, measure) - equippedDps;
              const pctDelta = dpsDelta / equippedDps;

              const replacementItem = _.find(
                allItems,
                {
                  id: actor?.droptimizerItem?.item?.id,
                  itemLevel: actor?.droptimizerItem?.item?.itemLevel,
                  encounterId,
                  bonusVariationName: actor?.droptimizerItem?.bonusVariationName,
                });

              encounterItems = _.reject(encounterItems, { id: actor.droptimizerItem.item.id });

              return {
                ...actor,
                droptimizerItem: {
                  ...actor.droptimizerItem,
                  item: replacementItem || actor.droptimizerItem.item,
                },
                dpsDelta,
                pctDelta,
                dpsDeltaClamped: _.max([0, dpsDelta]),
                pctDeltaClamped: _.max([0, pctDelta]),
                upgrade: pctDelta > upgradeThreshold,
                simulated: true,
              };
            }
            return {
              simulated: false,
              dpsDelta: 0,
              pctDelta: 0,
              dpsDeltaClamped: 0,
              pctDeltaClamped: 0,
              upgrade: false,
              droptimizerItem: {
                item,
              },
            };
          }),
          ['simulated', 'dpsDelta'],
          ['desc', 'desc'],
        );

        const numItems = _.size(actorsWithStats);
        const upgrades = _.filter(actorsWithStats, { upgrade: true });
        const numUpgrades = _.size(upgrades);

        return {
          encounterId,
          encounter: _.find(_.flatten(_.map(instanceLibrary || GameData.Instances, 'encounters')), {
            id: encounterId,
          }),
          total: numItems,
          upgrades,
          numUpgrades,
          upgradeChance: numUpgrades / numItems,
          actors: actorsWithStats,
          avgDps: _.sum(_.map(actorsWithStats, 'dpsDeltaClamped')) / numItems,
          avgPct: _.sum(_.map(actorsWithStats, 'pctDeltaClamped')) / numItems,
          maxDps: _.max(_.map(actorsWithStats, 'dpsDeltaClamped')) || 0,
          maxPct: _.max(_.map(actorsWithStats, 'pctDeltaClamped')) || 0,
        };
      }),
      stats => stats.total > 0,
    ),
    avgSort,
  );

  let rollRank = 1;
  // eslint-disable-next-line
  let count = 0;
  let prevRankDps = null;
  const errorMargin = 0.001 * equippedDps;
  encounterStats = _.map(encounterStats, e => {
    if (prevRankDps !== null) {
      const diff = prevRankDps - e.avgDps;
      if (diff > errorMargin) {
        rollRank += 1;
        // rollRank += count;
        count = 0;
        prevRankDps = e.avgDps;
      }
    } else {
      prevRankDps = e.avgDps;
    }
    // eslint-disable-next-line no-unused-vars
    count += 1;
    return {
      ...e,
      rank: rollRank,
    };
  });


  let fallbackSort = _.get(_.find(sortOptions, { key: 'priority' }), 'func');
  if (!showChances) {
    fallbackSort = _.get(_.find(sortOptions, { key: 'max' }), 'func');
  }
  const sortFunc = _.get(_.find(sortOptions, { key: droptimizerSort }), 'func', fallbackSort);

  encounterStats = _.sortBy(encounterStats, sortFunc);

  const avgDpsRange = _.max(_.map(encounterStats, 'avgDps')) - _.min(_.map(encounterStats, 'avgDps'));
  const maxDpsRange = _.max(_.map(encounterStats, 'maxDps')) - _.min(_.map(encounterStats, 'maxDps'));

  const iconPath = 'https://www.raidbots.com/static/images/EncounterJournal/orig';
  const bossIconPct = 1;
  const iconWidth = 128 * bossIconPct;
  const iconHeight = 64 * bossIconPct;
  //let hasGambling = false;

  const specInfo = getSpecInfo(droptimizerOptions.classId, droptimizerOptions.specId);

  return (
    <Panel theme="primary" pt={2} pb={1} mt={1}>
      <PanelHeader p={1} mb={1}>
        <Flex justify="space-between" align="center" auto>

          <Box>
            <Heading level={3}>{_.startCase(overrides.encounterType)} Summary</Heading>
          </Box>

          <Flex
            align="center"
            style={{
              fontSize: 13,
            }}
          >
            <Box>
              Sort
            </Box>
            {_.map(sortOptions, sort => {
              const selected = droptimizerSort === sort.key;
              return (
                <Box
                  key={sort.key}
                  ml={1}
                  style={{
                    padding: '4px 10px 4px 10px',
                    border: `1px solid ${selected ? config.colors.orange : 'black'}`,
                    backgroundColor: selected ? config.colors.gray : 'transparent',
                    color: selected ? config.colors.orange : null,
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => onChooseSort(sort.key)}
                >
                  {sort.name}
                </Box>
              );
            })}
          </Flex>

          <Checkbox
            name="relativeDps"
            label="Relative DPS"
            checked={relativeDps}
            onChange={onChangeRelativeDps}
            backgroundColor={config.colors.black}
            style={{ color: '#222', opacity: 1 }}
            pb={0}
            mb={0}
          />

        </Flex>
      </PanelHeader>

      <If
        condition={droptimizerOptions.upgradeEquipped}
        then={() => (
          <>
            <Text small align="center" mt={1} bold color={config.colors.blue}>
              Equipped gear was upgraded to the same level when possible
            </Text>
            <Divider my={1} />
          </>
        )}
      />

      {_.map(
        encounterStats,
        ({ rank, encounter, actors: encounterActors, maxDps, avgDps, maxPct, avgPct }) => {
          let gamblingInfo = null;

          const { gambling } = overrides;
          if (gambling) {
            //hasGambling = true;
            const { gambleCost, buyCost } = gambling;
            // check gambling
            const buyDps = maxDps / (buyCost / gambleCost);
            const gamblePctDiff = (avgDps - buyDps) / buyDps;
            gamblingInfo = {
              gambleCost,
              buyCost,
              buyDps,
              buyPct: buyDps / equippedDps,
              gambleEfficiency: gamblePctDiff,
            };
          }
          let iconListCols = 4;
          const expandedListTypes = [
            'dungeon',
            'vendor',
            'mplus-timewalking',
          ];
          if (_.includes(expandedListTypes, overrides.encounterType)) {
            iconListCols = 9;
          }
          if (overrides.instance?.type === 'mplus-timewalking') {
            iconListCols = 9;
          }

          return (
            <React.Fragment key={encounter.id}>
              <Flex justify="space-between" align="center" mt={1} style={{ minHeight: iconHeight - 4 }}>

                {/* Boss Icon */}
                <If
                  condition={overrides.encounterType === 'boss'}
                  then={() => (
                    <Box
                      p={0}
                      m={0}
                      style={{
                        height: iconHeight - 4,
                        width: iconWidth,
                        backgroundImage: `url("${iconPath}/${encounter.icon}.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'bottom -1px left -4px',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                  )}
                />

                {/* Boss Name */}

                <Box col={4}>
                  <Text bold size={16}>
                    {encounter.name}
                  </Text>
                </Box>

                {/* Icon List*/}

                <Box col={iconListCols} pl={1}>
                  <Flex column align="flex-start">
                    <Flex mt={1} wrap>
                      {_.map(encounterActors, ({ simulated, droptimizerItem, pctDelta, dpsDelta, upgrade }) => {
                        // This is all a nasty mess because we organize things using the ItemLibrary
                        let itemToShow = { ...droptimizerItem.item };
                        // find the right variation to show
                        const possibleItem = _.find(
                          droptimizerItems,
                          {
                            id: itemToShow.id,
                            itemLevel: itemToShow.itemLevel,
                            encounterId: droptimizerItem.encounter?.id,
                          },
                        );
                        if (_.size(possibleItem?.variations) > 1) {
                          const variation = _.find(possibleItem.variations, { slot: droptimizerItem.slot })?.item;
                          itemToShow = {
                            ...itemToShow,
                            gem_id: variation.gem_id,
                            enchant_id: variation.enchant_id,
                          };
                        }
                        return (
                          <Box mr={1} key={`${itemToShow.id}-${itemToShow.itemLevel}`}>
                            <Flex column align="center" style={{ minWidth: 28 }}>
                              <Box
                                style={{
                                  height: 28,
                                  filter: !simulated ? 'grayscale(1)' : null,
                                  position: 'relative',
                                }}
                              >
                                <WowItemIcon
                                  item={itemToShow}
                                  classId={classId}
                                  specId={specInfo?.id || specId}
                                  size={28}
                                  dull={!upgrade}
                                  setPieceIds={_.map(_.omit(baseGear, droptimizerItem.slot), 'id')}
                                  showGems
                                />
                                <If
                                  condition={itemToShow.offSpecItem}
                                  then={() => (
                                    <Box style={{ position: 'absolute', left: -2, top: -9 }}>
                                      <Tooltip
                                        title={(
                                          <Box>
                                            <Text size={14} bold>
                                              Item may not drop or be intended for your spec!
                                            </Text>
                                          </Box>
                                        )}
                                      >
                                        <Icon name="warning" size={12} fill={config.colors.orange} />
                                      </Tooltip>
                                    </Box>
                                  )}
                                />
                                <If
                                  condition={simulated && itemToShow.couldUpgrade}
                                  then={() => (
                                    <Box style={{ position: 'absolute', left: -8, bottom: -6, zIndex: 10 }}>
                                      <Tooltip
                                        title={(
                                          <Box>
                                            <Text size={14} bold>
                                              You have this item and can upgrade to this level
                                            </Text>
                                          </Box>
                                        )}
                                      >
                                        <Icon name="upgrade" size={12} color={config.colors.green} shadow />
                                      </Tooltip>
                                    </Box>
                                  )}
                                />
                              </Box>
                              <Text
                                size={10}
                                color={relativePctColor(pctDelta)}
                                style={{
                                  fontFamily: 'Tahoma',
                                  marginTop: 1,
                                }}
                              >
                                <If
                                  condition={simulated}
                                  then={() => (
                                    <span
                                      title={`${_.round(dpsDelta).toLocaleString()} DPS`}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {formatPct(pctDelta)}%
                                    </span>
                                  )}
                                  else={() => '-'}
                                />
                              </Text>
                            </Flex>
                          </Box>
                        );
                      })}
                    </Flex>
                  </Flex>
                </Box>

                {/* Expected Value */}

                <If
                  condition={showChances}
                  then={() => (
                    <Box col={2}>
                      <Flex column justify="center" align="center">
                        <Flex align="center">
                          <Text
                            bold
                            size={18}
                            color={dpsColor(avgDps)}
                            style={{ opacity: _.clamp(0.3 + (0.7 * (avgDps / avgDpsRange)), 0, 1) || 0.3 }}
                          >
                            {relativeDps ? formatPct(avgPct, 1, true) : formatDps(avgDps, true)}
                          </Text>
                          <Text
                            size={13}
                            style={{ marginLeft: 4 }}
                            color="#999"
                          >
                            {relativeDps ? '%' : 'DPS'}
                          </Text>
                        </Flex>
                        <Text size={12} color="#999" style={{ fontFamily: 'Tahoma' }}>
                          Expected Value
                        </Text>
                      </Flex>
                    </Box>
                  )}
                />

                {/* Buy Best EV */}

                {/*
                <If
                  condition={gambling}
                  then={() => (
                    <Box col={2}>
                      <Flex column justify="center" align="center">
                        <Flex align="center">
                          <Text
                            bold
                            size={18}
                            color={dpsColor(gamblingInfo.buyDps)}
                            style={{ opacity: _.clamp(0.3 + (0.7 * gamblingInfo.buyDps / maxDpsRange), 0, 1) || 0.3 }}
                          >
                            {
                              relativeDps
                                ? formatPct(gamblingInfo.buyPct, 1, true)
                                : formatDps(gamblingInfo.buyDps, true)
                            }
                          </Text>
                          <Text
                            size={13}
                            style={{ marginLeft: 4 }}
                            color="#999"
                          >
                            {relativeDps ? '%' : 'DPS'}
                          </Text>
                        </Flex>
                        <Text size={12} color="#999" style={{ fontFamily: 'Tahoma' }}>
                          Buy Best EV
                        </Text>
                      </Flex>
                    </Box>
                  )}
                />
                */}

                {/* Best */}

                <Box col={2}>
                  <Flex column justify="center" align="center">
                    <Flex align="center">
                      <Text
                        bold
                        size={18}
                        color={dpsColor(maxDps)}
                        style={{ opacity: _.clamp(0.3 + (0.7 * (maxDps / maxDpsRange)), 0, 1) || 0.3 }}
                      >
                        {relativeDps ? formatPct(maxPct, 1, true) : formatDps(maxDps, true)}
                      </Text>
                      <Text
                        size={13}
                        style={{ marginLeft: 4 }}
                        color="#999"
                      >
                        {relativeDps ? '%' : 'DPS'}
                      </Text>
                    </Flex>
                    <Text size={12} color="#999" style={{ fontFamily: 'Tahoma' }}>
                      Best
                    </Text>
                  </Flex>
                </Box>

                {/* Buy or Gamble? */}

                <If
                  condition={gambling}
                  then={() => (
                    <Box style={{ width: 100 }}>
                      <Flex column justify="center" align="center">
                        <Flex align="center">
                          <Text bold size={16} color={config.colors.white}>
                            <If
                              condition={avgDps < 0.1}
                              then={() => '-'}
                              else={() => (avgDps > gamblingInfo.buyDps ? 'Gamble' : 'Buy')}
                            />
                          </Text>
                        </Flex>
                        <Text size={12} color="#999" style={{ fontFamily: 'Tahoma' }}>
                          Gamble/Buy
                        </Text>
                      </Flex>
                    </Box>
                  )}
                />

                {/* Priority */}

                <If
                  condition={showChances}
                  then={() => (
                    <Box style={{ width: 100 }}>
                      <Flex column justify="center" align="center">
                        <Flex align="center">
                          <Text bold size={21} color={config.colors.orange}>
                            {avgDps < 0.1 ? '-' : rank}
                          </Text>
                        </Flex>
                        <Text size={12} color="#999" style={{ fontFamily: 'Tahoma' }}>
                          Priority
                        </Text>
                      </Flex>
                    </Box>
                  )}
                />

              </Flex>

              <Divider m={0} p={0} />

            </React.Fragment>
          );
        })}

      {/*
      <If
        condition={hasGambling}
        then={() => (
          <Text small align="center" bold mt={1}>
            Buy Best EV is normalized to gambling cost (Best Drop DPS divided by the higher cost)
          </Text>
        )}
      />
      */}
      <Text small align="center" mt={1} bold>
        DPS compared to your current gear.
        Highlighted icons indicate {formatPct(upgradeThreshold, 2)}% or better DPS increase.
        &nbsp;
        <a
          href="https://support.raidbots.com/article/59-droptimizer-how-does-it-work"
          target="_blank"
          rel="noopener noreferrer"
        >
          More Info
        </a>
      </Text>
    </Panel>
  );
};


DroptimizerPanel.propTypes = {
  droptimizerItems: PropTypes.array.isRequired,
  droptimizerOptions: PropTypes.object.isRequired,
  actors: PropTypes.array.isRequired,
  baseGear: PropTypes.object.isRequired,
  classId: PropTypes.number,
  specId: PropTypes.number,
  errorMarginPct: PropTypes.number.isRequired,
  relativeDps: PropTypes.bool.isRequired,
  droptimizerSort: PropTypes.string.isRequired,
  itemLibrary: PropTypes.array,
  instanceLibrary: PropTypes.array,
  measure: PropTypes.string.isRequired,

  onChooseSort: PropTypes.func.isRequired,
  onChangeRelativeDps: PropTypes.func.isRequired,
};

DroptimizerPanel.defaultProps = {
  classId: null,
  specId: null,
  itemLibrary: null,
  instanceLibrary: null,
};

const mapStateToProps = state => ({
  droptimizerSort: reportSelectors.droptimizerSort(state),
  relativeDps: reportSelectors.droptimizerRelativeDps(state),
});

const mapDispatchToProps = {
  onChangeRelativeDps: actions.toggleReportDroptimizerRelativeDps,
  onChooseSort: actions.chooseReportDroptimizerSort,
};


export default connect(mapStateToProps, mapDispatchToProps)(DroptimizerPanel);
